import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ChevronRight, ArrowRight, Cpu, TabletSmartphone, Gem,
  ShieldCheck, Search, PenTool, CodeXml, Rocket,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLang } from '../context/LangContext';
import HeroIllustration from '../components/shared/HeroIllustration';
import useReveal from '../hooks/useReveal';
import countries from '../constants/countries';
import { useLocation } from '../context/LocationContext';

/* ─────────────────────────────────────────────
   GLASS TOKENS — لسه مستخدمة كـ reference لأي حسابات
   inline محتاجة (مش بتترسم منها classes مباشرة غير في
   GlassBox، اللي دلوقتي بيستخدم CSS classes بدل الحساب هنا)
───────────────────────────────────────────── */
const glass = {
  light: {
    bg:     'rgba(255,255,255,0.18)',
    border: '1px solid rgba(255,255,255,0.35)',
    blur:   'blur(14px)',
    shadow: '0 8px 32px rgba(201,169,110,0.10), inset 0 1px 0 rgba(255,255,255,0.5)',
  },
  input: {
    bg:     'rgba(255,255,255,0.22)',
    border: '1.5px solid rgba(255,255,255,0.40)',
    blur:   'blur(10px)',
  },
};

/* ── Helper: Glass Box (background/border/blur/shadow دلوقتي CSS classes) ── */
const GlassBox = React.memo(React.forwardRef(({ children, variant = 'light', className = '', style, ...rest }, ref) => (
  <div ref={ref} className={`glass-${variant}${className ? ` ${className}` : ''}`} style={style} {...rest}>
    {children}
  </div>
)));

/* ══════════════════════════════════════════════════════
   LIQUID GLASS HOOK — زي ما هي، شغالة بس وقت اللمس/الضغط
══════════════════════════════════════════════════════ */
const useLiquidGlass = ({ stiffness = 280, damping = 18, pressScale = 0.055 } = {}) => {
  const ref = React.useRef(null);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let scaleX = 1, scaleY = 1;
    let velX = 0, velY = 0;
    let targetX = 1, targetY = 1;
    let originX = '50%', originY = '50%';
    let pressing  = false;
    let rafId;
    let startY    = 0;
    let didScroll = false;

    const spring = (cur, tgt, vel) => {
      const force = -stiffness * (cur - tgt) - damping * vel;
      const nVel  = vel + (force / 1) * 0.016;
      const nPos  = cur + nVel * 0.016;
      return { pos: nPos, vel: nVel };
    };

    const animate = () => {
      const rx = spring(scaleX, targetX, velX);
      const ry = spring(scaleY, targetY, velY);
      scaleX = rx.pos; velX = rx.vel;
      scaleY = ry.pos; velY = ry.vel;

      el.style.transformOrigin = `${originX} ${originY}`;
      el.style.transform = `scaleX(${scaleX.toFixed(4)}) scaleY(${scaleY.toFixed(4)})`;

      const done = !pressing
        && Math.abs(scaleX - 1) < 0.0005 && Math.abs(scaleY - 1) < 0.0005
        && Math.abs(velX) < 0.0005 && Math.abs(velY) < 0.0005;

      if (!done) rafId = requestAnimationFrame(animate);
      else {
        scaleX = 1; scaleY = 1;
        el.style.transform      = 'none';
        el.style.transformOrigin = '50% 50%';
      }
    };

    const onPress = e => {
      if (e.touches) {
        startY    = e.touches[0].clientY;
        didScroll = false;
      }
      pressing = true;
      const r  = el.getBoundingClientRect();
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      const cy = e.touches ? e.touches[0].clientY : e.clientY;
      const px = (cx - r.left) / r.width;
      const py = (cy - r.top)  / r.height;
      originX  = `${(px * 100).toFixed(1)}%`;
      originY  = `${(py * 100).toFixed(1)}%`;
      targetX  = 1 - pressScale * (px < 0.5 ? (1 - px) : px);
      targetY  = 1 - pressScale * (py < 0.5 ? (1 - py) : py);
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(animate);
    };

    const onTouchMove = e => {
      if (Math.abs(e.touches[0].clientY - startY) > 8) {
        didScroll = true;
        pressing  = false;
        targetX   = 1; targetY = 1;
        cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(animate);
      }
    };

    const onRelease = e => {
      if (!pressing) return;
      pressing = false;
      targetX  = 1; targetY = 1;
      velX += (1 - scaleX) * 4;
      velY += (1 - scaleY) * 4;
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(animate);

      if (e?.type === 'touchend' && !didScroll) {
        el.click();
      }
    };

    el.addEventListener('mousedown',  onPress,     { passive: true });
    el.addEventListener('touchstart', onPress,     { passive: true });
    el.addEventListener('touchmove',  onTouchMove, { passive: true });
    window.addEventListener('mouseup',  onRelease);
    window.addEventListener('touchend', onRelease);

    return () => {
      cancelAnimationFrame(rafId);
      el.removeEventListener('mousedown',  onPress);
      el.removeEventListener('touchstart', onPress);
      el.removeEventListener('touchmove',  onTouchMove);
      window.removeEventListener('mouseup',  onRelease);
      window.removeEventListener('touchend', onRelease);
    };
  }, [stiffness, damping, pressScale]);

  return ref;
};

/* ── Shared Components ── */
const Tag = React.memo(({ children, style }) => (
  <span className="tag-el" style={style}>{children}</span>
));

const Btn = React.memo(({ href, to, primary, children, style }) => {
  const lgRef = useLiquidGlass({ pressScale: 0.045 });
  const className = `btn-el ${primary ? 'btn-primary' : 'btn-secondary'}`;
  if (to)   return <Link ref={lgRef} to={to} className={className} style={style}>{children}</Link>;
  return <a ref={lgRef} href={href} className={className} style={style}>{children}</a>;
});

/* ── Service Card ── */
const ServiceCard = React.memo(({ icon, title, desc }) => {
  const lgRef = useLiquidGlass({ pressScale: 0.04, stiffness: 260, damping: 16 });
  return (
    <GlassBox ref={lgRef} variant="light" className="service-card">
      <div className="service-icon-box">{icon}</div>
      <h3 className="service-title">{title}</h3>
      <p className="service-desc">{desc}</p>
    </GlassBox>
  );
});

/* ── View All Button ── */
const ViewAllBtn = React.memo(({ isRtl }) => {
  const lgRef = useLiquidGlass({ pressScale: 0.05, stiffness: 300, damping: 18 });
  return (
    <Link ref={lgRef} to="/services" className="view-all-btn">
      {isRtl ? 'التفاصيل' : 'Details'}
      {isRtl
        ? <ArrowRight size={16} strokeWidth={1.5} style={{ transform:'rotate(180deg)' }} />
        : <ArrowRight size={16} strokeWidth={1.5} />}
    </Link>
  );
});

/* ══════════════════════════════════════════════════════
   GOLDEN DOTS — نفس المنطق بالظبط (12 نقطة، CSS animation)،
   بس شلت الـ <style> tag اللي كان بيتكرر جوه الـ DOM 4 مرات
   (مرة لكل GoldenDots instance في الصفحة) — الكيفريم دلوقتي
   في ملف الـ CSS مرة واحدة بس.
══════════════════════════════════════════════════════ */
const GoldenDots = React.memo(() => {
  const dots = useMemo(() => Array.from({ length: 12 }).map((_, i) => ({
    id: i,
    coreSize:  Math.random() * 2 + 1.5,
    hazeSize:  Math.random() * 10 + 8,
    left:      `${Math.random() * 100}%`,
    top:       `${Math.random() * 100}%`,
    duration:  (Math.random() * 2.5 + 2).toFixed(2),
    delay:     (Math.random() * 2).toFixed(2),
    glowShadow: `0 0 ${Math.random() * 8 + 4}px rgba(201,169,110,0.9)`,
  })), []);

  return (
    <div className="golden-dots-container">
      {dots.map(dot => (
        <div
          key={dot.id}
          className="dot-wrapper"
          style={{ left: dot.left, top: dot.top, animation: `dotTwinkle ${dot.duration}s ease-in-out ${dot.delay}s infinite` }}
        >
          <div className="dot-core" style={{ width: dot.coreSize, height: dot.coreSize, boxShadow: dot.glowShadow }} />
          <div className="dot-haze" style={{ width: dot.hazeSize, height: dot.hazeSize }} />
        </div>
      ))}
    </div>
  );
});

/* ══════════════════════════════════════════════════════
   HERO
══════════════════════════════════════════════════════ */
const HeroTextPanel = React.memo(() => {
  const { t, isRtl } = useLang();
  const lgRef = useLiquidGlass({ pressScale: 0.025, stiffness: 200, damping: 20 });
  return (
    <GlassBox ref={lgRef} variant="medium" className="section-animate hero-text-panel">
      <div className="hero-badge">
        <span className="hero-badge-dot-wrap">
          <span className="hero-badge-dot-ping" />
          <span className="hero-badge-dot-core" />
        </span>
        {t.hero.badge}
      </div>
      <h1 className="hero-title">
        {t.hero.title}
        <span className="hero-title-accent">{t.hero.titleAccent}</span>
        {t.hero.titleEnd}
      </h1>
      <p className="hero-sub">{t.hero.sub}</p>
      <div className="hero-cta-row">
        <Btn to="/contact" primary>
          {t.hero.cta1}
          {isRtl
            ? <ArrowRight size={16} strokeWidth={1.5} style={{ transform:'rotate(180deg)' }} />
            : <ChevronRight size={16} strokeWidth={1.5} />}
        </Btn>
        <Btn to="/services">{t.hero.cta2}</Btn>
      </div>
    </GlassBox>
  );
});

const HeroIllustrationPanel = React.memo(() => {
  const lgRef = useLiquidGlass({ pressScale: 0.02, stiffness: 180, damping: 22 });
  return (
    <GlassBox ref={lgRef} variant="medium" className="hero-illustration hero-illustration-panel">
      <HeroIllustration />
    </GlassBox>
  );
});

const HeroSection = () => {
  const { isRtl } = useLang();
  return (
    <section className={`hero-section ${isRtl ? 'is-rtl' : ''}`}>
      <div className="decor-blur-circle hero-circle-1" />
      <div className="decor-blur-circle hero-circle-2" />
      <GoldenDots />
      <div className="hero-grid hero-grid-inner">
        <HeroTextPanel />
        <HeroIllustrationPanel />
      </div>
    </section>
  );
};

/* ══════════════════════════════════════════════════════
   SERVICES PREVIEW
══════════════════════════════════════════════════════ */
const ServicesPreview = () => {
  const { t, isRtl } = useLang();
  const ref = useReveal();

  const icons = useMemo(() => [
    <Cpu size={26} strokeWidth={1.2} />,
    <TabletSmartphone size={26} strokeWidth={1.2} />,
    <Gem size={26} strokeWidth={1.2} />,
  ], []);
  const preview = t.services.items.slice(0, 3);

  return (
    <section className="services-section">
      <div className="decor-blur-circle services-circle-1" />
      <div className="decor-blur-circle services-circle-2" />
      <GoldenDots />

      <div className="section-inner">
        <div ref={ref} className="section-animate">
          <div className="services-header">
            <div>
              <Tag>{t.services.tag}</Tag>
              <h2 className="services-title">{t.services.title}</h2>
            </div>
            <ViewAllBtn isRtl={isRtl} />
          </div>

          <div className="services-grid">
            {preview.map((s, i) => (
              <ServiceCard key={i} icon={icons[i]} title={s.title} desc={s.desc} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ── Process Step ── */
const ProcessStep = React.memo(({ step, icon, index }) => {
  const circleRef = useLiquidGlass({ pressScale: 0.06, stiffness: 320, damping: 20 });
  const cardRef   = useLiquidGlass({ pressScale: 0.035, stiffness: 240, damping: 16 });
  return (
    <div className="process-step" style={{ animationDelay: `${index * 0.5}s` }}>
      <div className="process-step-number">0{index + 1}</div>

      <div ref={circleRef} className="process-icon-circle" style={{ color: index % 2 === 0 ? '#fff' : '#C9A96E' }}>
        <div className="process-icon-ring" />
        {icon}
      </div>

      <GlassBox ref={cardRef} variant="dark" className="process-card">
        <h3 className="process-card-title">{step.title}</h3>
        <p className="process-card-desc">{step.desc}</p>
      </GlassBox>
    </div>
  );
});

/* ══════════════════════════════════════════════════════
   PROCESS
══════════════════════════════════════════════════════ */
const ProcessSection = () => {
  const { t, isRtl } = useLang();

  const stepIcons = useMemo(() => [
    <Search  size={32} strokeWidth={1} />,
    <PenTool size={32} strokeWidth={1} />,
    <CodeXml size={32} strokeWidth={1} />,
    <Rocket  size={32} strokeWidth={1} />,
  ], []);

  return (
    <section className="process-section">
      <div className="decor-blur-circle process-circle-1" />
      <div className="decor-blur-circle process-circle-2" />
      <GoldenDots />

      <div className="section-inner">
        <div className="process-header">
          <div className="process-header-row">
            <span className="process-header-line" />
            <Tag style={{ marginBottom: 0, letterSpacing: '0.15em' }}>{t.process.tag}</Tag>
            <span className="process-header-line" />
          </div>
          <h2 className="process-title">{t.process.title}</h2>
        </div>

        <div className="process-grid-container">
          <div className="process-line-desktop">
            <div className={`process-line-runner-x ${isRtl ? 'is-rtl' : ''}`} />
          </div>

          <div className="process-line-mobile">
            <div className="process-line-runner-y" />
          </div>

          {t.process.steps.map((step, i) => (
            <ProcessStep key={i} step={step} icon={stepIcons[i]} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

/* ══════════════════════════════════════════════════════
   QUICK CONTACT
══════════════════════════════════════════════════════ */
const QuickContactSection = () => {
  const { t, isRtl } = useLang();
  const { countryCode: detectedCode } = useLocation();
  const ref = useReveal();

  const [formStatus,   setFormStatus]   = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [formData,     setFormData]     = useState({ name:'', email:'', phone:'', countryCode:'+20', message:'' });

  useEffect(() => {
    const handler = e => { if (!e.target.closest('.country-dropdown')) setDropdownOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    setFormData(p => ({ ...p, countryCode: detectedCode }));
  }, [detectedCode]);

  const API = 'https://aura-crm.runasp.net';

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setFormStatus('sending');

    try {
      const res = await fetch(`${API}/api/public/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: formData.name,
          email: formData.email,
          phone: formData.countryCode + formData.phone.replace(/\s/g, ''),
          message: formData.message,
        }),
      });

      if (res.ok) {
        setFormStatus('success');
        setFormData({ name: '', email: '', phone: '', countryCode: '+20', message: '' });
      } else {
        setFormStatus('error');
      }
    } catch (err) {
      setFormStatus('error');
    }
  }, [formData]);

  const selectedCountry = countries.find(c => c.code === formData.countryCode);
  const submitRef = useLiquidGlass({ pressScale: 0.04, stiffness: 300, damping: 18 });

  return (
    <section className="contact-section">
      <div className="decor-blur-circle contact-circle-1" />
      <div className="decor-blur-circle contact-circle-2" />
      <GoldenDots />

      <div className="section-inner contact-inner">
        <div ref={ref} className="section-animate">

          <div className="contact-header">
            <Tag>{isRtl ? 'دعنا نتحدث' : "Let's Talk"}</Tag>
            <h2 className="contact-title">
              {isRtl ? 'جاهز تبدأ مشروعك؟' : 'Ready to start your project?'}
            </h2>
            <p className="contact-sub">
              {isRtl ? 'سجل بياناتك وسنقوم بالتواصل معك في أقرب وقت لبدء رحلة نجاحك.' : 'Drop your details below and our team will get back to you shortly.'}
            </p>
          </div>

          <GlassBox variant="medium" className="contact-form-box">
            {formStatus === 'success' ? (
              <div className="contact-success">
                <div className="contact-success-icon">
                  <ShieldCheck size={40} strokeWidth={1.5} />
                </div>
                <h3 className="contact-success-title">{t.contact.success}</h3>
                <p className="contact-success-sub">{t.contact.successSub}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="contact-form">

                <div className="contact-form-row">
                  {[
                    { key:'name',  label:t.contact.formName,  type:'text',  placeholder: isRtl?'محمد أحمد':'John Doe',
                      onChange: v => /^[\u0600-\u06FFa-zA-Z\s]*$/.test(v) && setFormData(p=>({...p,name:v})) },
                    { key:'email', label:t.contact.formEmail, type:'email', placeholder:'hello@example.com',
                      onChange: v => setFormData(p=>({...p,email:v})) },
                  ].map(({ key, label, type, placeholder, onChange }) => (
                    <div key={key}>
                      <label className="contact-label">{label}</label>
                      <input
                        required type={type} placeholder={placeholder} value={formData[key]}
                        onChange={e => onChange(e.target.value)}
                        className="contact-input"
                      />
                    </div>
                  ))}
                </div>

                <div>
                  <label className="contact-label">
                    {isRtl ? 'رقم الهاتف' : 'Phone Number'}
                  </label>
                  <div className="contact-phone-row">
                    <div className="country-dropdown">
                      <div
                        onClick={() => setDropdownOpen(o => !o)}
                        className="country-toggle"
                      >
                        <img src={`https://flagcdn.com/w20/${selectedCountry?.iso}.png`} width="20" height="14" className="country-flag" />
                        <span className="country-code-text">{formData.countryCode}</span>
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className={`country-chevron ${dropdownOpen ? 'is-open' : ''}`}>
                          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>

                      {dropdownOpen && (
                        <div className="country-menu">
                          {countries.map(c => (
                            <div
                              key={c.code}
                              onClick={() => { setFormData(p=>({...p,countryCode:c.code})); setDropdownOpen(false); }}
                              className={`dropdown-item ${formData.countryCode===c.code ? 'is-selected' : ''}`}
                            >
                              <img src={`https://flagcdn.com/w20/${c.iso}.png`} width="20" height="14" className="country-flag" />
                              <span className="dropdown-item-code">{c.code}</span>
                              <span>{c.name}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <input
                      required type="tel" dir="ltr"
                      placeholder={selectedCountry?.placeholder || '000 000 0000'}
                      value={formData.phone}
                      onChange={e => { const v=e.target.value; if(/^[0-9\s]*$/.test(v)) setFormData(p=>({...p,phone:v})); }}
                      className="contact-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="contact-label">{t.contact.formMsg}</label>
                  <textarea
                    required rows={4}
                    placeholder={isRtl ? 'اكتب تفاصيل مشروعك أو استفسارك هنا...' : 'Tell us about your project or inquiry...'}
                    value={formData.message}
                    onChange={e => setFormData(p=>({...p,message:e.target.value}))}
                    className="contact-input contact-textarea"
                  />
                </div>

                <button
                  ref={submitRef}
                  type="submit"
                  className={`submit-btn ${formStatus==='sending' ? 'is-sending' : ''}`}
                  disabled={formStatus==='sending'}
                >
                  {formStatus==='sending'
                    ? (isRtl ? 'جاري الإرسال...' : 'Sending...')
                    : (isRtl ? 'إرسال طلب استشارة' : 'Send Consultation Request')}
                  {formStatus!=='sending' && <ArrowRight size={18} strokeWidth={1.5} style={{ transform:isRtl?'rotate(180deg)':'none' }} />}
                </button>

              </form>
            )}
          </GlassBox>
        </div>
      </div>
    </section>
  );
};

/* ══════════════════════════════════════════════════════
   PAGE
══════════════════════════════════════════════════════ */
const HomePage = () => (
  <>
    <HeroSection />
    <ServicesPreview />
    <ProcessSection />
    <QuickContactSection />
  </>
);

export default HomePage;